'use client'

/* eslint-disable @next/next/no-img-element */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import TypewriterText from '../../../../../components/visual-runtime/TypewriterText'
import { translateText } from '../../../shared/api/translate'
import HydrateText from '../../../shared/components/HydrateText'
import ForumNickText from '../../../shared/components/ForumNickText'
import { formatCount as formatCompactCount } from '../../../shared/utils/counts'
import {
  QuantumFamilyBadgeTitleSvg,
  QuantumFamilyCounterPill,
} from '../../subscriptions/components/FollowersCounterInline'
import { resolveProfileAccountId, writeProfileAlias } from '../utils/profileCache'
import { MetaMarketGiftIcon } from '../../../../../components/MetaMarketIcons'
import { runAuthorizedClientAction } from '../../../../../lib/authActionGateClient'
import StarButton from '../../ui/components/StarButton'
import { AvatarBadgeOverlay } from './VipFlipBadge'
import {
  buildForumDmHref,
  clearPendingForumDmUserId,
  writePendingForumDmUserId,
} from '../../../../../lib/forumDmNavigation.js'

const USER_INFO_ONLINE_WINDOW_MS = 75 * 1000
const USER_INFO_ENTER_MS = 420
const USER_INFO_EXIT_MS = 320
const USER_INFO_EXIT_SAFETY_MS = 96
const USER_INFO_STACK_Z = 2147482950

function prefersReducedMotion() {
  try {
    return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true
  } catch {
    return false
  }
}

function numericTs(value) {
  const n = Number(value || 0)
  return Number.isFinite(n) && n > 0 ? n : 0
}

function isAvatarImageSource(value) {
  const source = String(value || '').trim()
  return /^(?:https?:|data:image\/|blob:|\/)/iu.test(source)
}

function readViewerId() {
  try {
    const w = typeof window !== 'undefined' ? window : {}
    const ls = typeof localStorage !== 'undefined' ? localStorage : null
    const fromWindow = w.__AUTH_ACCOUNT__ || w.__ASHER_ID__ || w.__QL7_UID__ || w.__FORUM_USER__
    if (fromWindow) return String(fromWindow).trim()
    if (!ls) return ''
    return String(
      ls.getItem('account') ||
      ls.getItem('wallet') ||
      ls.getItem('asherId') ||
      ls.getItem('ql7_uid') ||
      ls.getItem('forum_user_id') ||
      '',
    ).trim()
  } catch {
    return ''
  }
}

export default function UserInfoPopover({
  open,
  onClose,
  rawUserId,
  userPreview,
  t,
  renderRich,
  formatCountFn,
  onOpenUserPosts,
  onOpenUserTopics,
  onOpenSubscriptions,
  onOpenDirectMessage,
  onOpenGiftFlow,
  viewerId,
  starredAuthors,
  onToggleStar,
}) {
  const cacheRef = useRef(new Map())
  const aliasRef = useRef(new Map())
  const inFlightRef = useRef(new Map())
  const statsInFlightRef = useRef(new Map())
  const popoverRef = useRef(null)
  const motionEnterRafARef = useRef(null)
  const motionEnterRafBRef = useRef(null)
  const motionExitTimerRef = useRef(null)
  const motionSkipExitRef = useRef(false)
  const [status, setStatus] = useState('idle')
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [translatedBio, setTranslatedBio] = useState(null)
  const [showOriginal, setShowOriginal] = useState(false)
  const [translateBusy, setTranslateBusy] = useState(false)
  const [bioTranslationAnimation, setBioTranslationAnimation] = useState({ src: '', key: 0 })
  const [presenceOnline, setPresenceOnline] = useState(false)
  const [motionMounted, setMotionMounted] = useState(false)
  const [motionPhase, setMotionPhase] = useState('closed')
  const [motionUserId, setMotionUserId] = useState('')

  const formatCount = typeof formatCountFn === 'function' ? formatCountFn : formatCompactCount
  const safeRich = typeof renderRich === 'function' ? renderRich : (s) => String(s || '')

  const clearMotionEnterFrames = useCallback(() => {
    if (motionEnterRafARef.current) {
      cancelAnimationFrame(motionEnterRafARef.current)
      motionEnterRafARef.current = null
    }
    if (motionEnterRafBRef.current) {
      cancelAnimationFrame(motionEnterRafBRef.current)
      motionEnterRafBRef.current = null
    }
  }, [])

  const clearMotionExitTimer = useCallback(() => {
    if (motionExitTimerRef.current) {
      clearTimeout(motionExitTimerRef.current)
      motionExitTimerRef.current = null
    }
  }, [])

  const finishMotionExit = useCallback(() => {
    clearMotionEnterFrames()
    clearMotionExitTimer()
    setMotionMounted(false)
    setMotionPhase('closed')
    setMotionUserId('')
    setStatus('idle')
    setError(null)
    setData(null)
    setTranslatedBio(null)
    setShowOriginal(false)
    setTranslateBusy(false)
    setPresenceOnline(false)
  }, [clearMotionEnterFrames, clearMotionExitTimer])

  const registerAction = useCallback(() => {}, [])

  const notifySearchPopoverNavigation = useCallback((action) => {
    try {
      window.dispatchEvent(new CustomEvent('forum:search-popover-close', {
        detail: { source: 'user-info-popover', action: String(action || '') },
      }))
    } catch {}
  }, [])

  const getCachedUserInfo = useCallback((uid) => {
    const rawKey = String(uid || '').trim()
    if (!rawKey) return null
    const resolved = resolveProfileAccountId(rawKey)
    const accountId = aliasRef.current.get(rawKey) || (resolved && resolved !== rawKey ? resolved : null)
    if (accountId && cacheRef.current.has(accountId)) {
      return cacheRef.current.get(accountId)
    }
    return null
  }, [])

  const fetchUserInfo = useCallback(
    async (uid, { force = false } = {}) => {
      const rawKey = String(uid || '').trim()
      if (!rawKey) throw new Error('missing_user_id')

      const cached = force ? null : getCachedUserInfo(rawKey)
      if (cached) return cached

      const resolved = resolveProfileAccountId(rawKey)
      const aliasKey = aliasRef.current.get(rawKey) || (resolved && resolved !== rawKey ? resolved : null)
      const inFlightKey = `${aliasKey || rawKey}:${force ? 'refresh' : 'default'}`
      if (inFlightRef.current.has(inFlightKey)) {
        return inFlightRef.current.get(inFlightKey)
      }

      const promise = (async () => {
        const res = await fetch(`/api/profile/user-popover?uid=${encodeURIComponent(rawKey)}`, {
          method: 'GET',
          cache: 'no-store',
        })
        const json = await res.json().catch(() => null)
        if (!res.ok || !json?.ok) {
          throw new Error(json?.error || `HTTP ${res.status}`)
        }

        const accountId = String(json.accountId || json.userId || rawKey).trim()
        const payload = {
          accountId,
          nickname: String(json?.nickname || userPreview?.nickname || '').trim(),
          icon: String(json?.icon || userPreview?.icon || userPreview?.avatar || '').trim(),
          vipActive: typeof json?.vipActive === 'boolean' ? json.vipActive : !!userPreview?.vipActive,
          officialStatus: json?.officialStatus === true,
          about: String(
            json?.about ||
            userPreview?.about ||
            userPreview?.description ||
            userPreview?.bio ||
            '',
          ),
          lastActiveAt: Number(json?.lastActiveAt || 0),
          presenceOfflineAt: Number(json?.presenceOfflineAt || 0),
          serverNow: Number(json?.serverNow || Date.now()),
          stats: {
            followers: Number(json?.stats?.followers || 0),
            following: Number(
              json?.stats?.following ??
              json?.stats?.followingCount ??
              json?.followingCount ??
              json?.counts?.following ??
              0
            ),
            posts: Number(json?.stats?.posts || 0),
            topics: Number(json?.stats?.topics || 0),
            likes: Number(json?.stats?.likes || 0),
          },
        }

        if (accountId) {
          cacheRef.current.set(accountId, payload)
          aliasRef.current.set(rawKey, accountId)
          writeProfileAlias(rawKey, accountId)
        }

        return payload
      })()

      inFlightRef.current.set(inFlightKey, promise)
      try {
        return await promise
      } finally {
        inFlightRef.current.delete(inFlightKey)
      }
    },
    [
      getCachedUserInfo,
      userPreview?.about,
      userPreview?.avatar,
      userPreview?.bio,
      userPreview?.description,
      userPreview?.icon,
      userPreview?.nickname,
      userPreview?.vipActive,
    ],
  )

  const applyPresencePayload = useCallback((payload) => {
    const lastActiveAt = numericTs(payload?.lastActiveAt)
    const presenceOfflineAt = numericTs(payload?.presenceOfflineAt)
    const serverNow = numericTs(payload?.serverNow) || Date.now()
    setPresenceOnline(
      !!lastActiveAt &&
      (!presenceOfflineAt || presenceOfflineAt < lastActiveAt) &&
      serverNow - lastActiveAt <= USER_INFO_ONLINE_WINDOW_MS
    )
  }, [])

  const fetchUserStatsSlice = useCallback(async (uid) => {
    const rawKey = String(uid || '').trim()
    if (!rawKey) throw new Error('missing_user_id')

    const resolved = resolveProfileAccountId(rawKey)
    const aliasKey = aliasRef.current.get(rawKey) || (resolved && resolved !== rawKey ? resolved : null)
    const inFlightKey = aliasKey || rawKey
    if (statsInFlightRef.current.has(inFlightKey)) return statsInFlightRef.current.get(inFlightKey)

    const promise = (async () => {
      const qs = new URLSearchParams()
      qs.set('uid', rawKey)
      qs.set('stats', '1')
      const res = await fetch(`/api/profile/user-popover?${qs.toString()}`, {
        method: 'GET',
        cache: 'no-store',
      })
      const json = await res.json().catch(() => null)
      if (!res.ok || !json?.ok || json?.statsOnly !== true) throw new Error(json?.error || `HTTP ${res.status}`)

      const accountId = String(json.accountId || json.userId || aliasKey || rawKey).trim()
      const previous = cacheRef.current.get(accountId) || (aliasKey ? cacheRef.current.get(aliasKey) : null)
      if (!accountId || !previous) return null
      const nextStats = { ...(previous.stats || {}) }
      if (json?.familyAvailable === true) {
        nextStats.followers = Number(json?.stats?.followers || 0)
        nextStats.following = Number(json?.stats?.following || 0)
      }
      if (json?.statsAvailable === true) {
        nextStats.posts = Number(json?.stats?.posts || 0)
        nextStats.topics = Number(json?.stats?.topics || 0)
        nextStats.likes = Number(json?.stats?.likes || 0)
      }
      const next = {
        ...previous,
        accountId,
        lastActiveAt: Number(json?.lastActiveAt || 0),
        presenceOfflineAt: Number(json?.presenceOfflineAt || 0),
        serverNow: Number(json?.serverNow || Date.now()),
        stats: nextStats,
      }
      cacheRef.current.set(accountId, next)
      aliasRef.current.set(rawKey, accountId)
      writeProfileAlias(rawKey, accountId)
      return next
    })()

    statsInFlightRef.current.set(inFlightKey, promise)
    try { return await promise } finally { statsInFlightRef.current.delete(inFlightKey) }
  }, [])

  const handleRetry = useCallback(() => {
    if (!rawUserId) return
    registerAction()
    setStatus('loading')
    setError(null)
    setData(null)
    fetchUserInfo(rawUserId)
      .then((payload) => {
        setData(payload)
        applyPresencePayload(payload)
        setStatus('ready')
      })
      .catch((err) => {
        setError(err?.message || 'error')
        setStatus('error')
      })
  }, [applyPresencePayload, fetchUserInfo, rawUserId, registerAction])

  useEffect(() => {
    const targetUserId = String(rawUserId || '').trim()

    if (open && targetUserId) {
      clearMotionEnterFrames()
      clearMotionExitTimer()
      motionSkipExitRef.current = false
      setMotionUserId(targetUserId)
      setMotionMounted(true)
      setMotionPhase('pre-enter')
      return undefined
    }

    if (open || !motionMounted) return undefined

    if (motionSkipExitRef.current) {
      motionSkipExitRef.current = false
      finishMotionExit()
      return undefined
    }

    clearMotionEnterFrames()
    try {
      const activeElement = document.activeElement
      if (popoverRef.current?.contains?.(activeElement)) activeElement?.blur?.()
    } catch {}

    if (prefersReducedMotion()) {
      finishMotionExit()
      return undefined
    }

    setMotionPhase('exit')
    motionExitTimerRef.current = setTimeout(
      finishMotionExit,
      USER_INFO_EXIT_MS + USER_INFO_EXIT_SAFETY_MS,
    )

    return () => clearMotionExitTimer()
  }, [
    clearMotionEnterFrames,
    clearMotionExitTimer,
    finishMotionExit,
    motionMounted,
    open,
    rawUserId,
  ])

  useEffect(() => {
    if (!open || !motionMounted || motionPhase !== 'pre-enter') return undefined

    clearMotionEnterFrames()

    if (prefersReducedMotion()) {
      setMotionPhase('open')
      return undefined
    }

    motionEnterRafARef.current = requestAnimationFrame(() => {
      motionEnterRafARef.current = null
      motionEnterRafBRef.current = requestAnimationFrame(() => {
        motionEnterRafBRef.current = null
        setMotionPhase('open')
      })
    })

    return () => clearMotionEnterFrames()
  }, [clearMotionEnterFrames, motionMounted, motionPhase, open])

  useEffect(() => () => {
    clearMotionEnterFrames()
    clearMotionExitTimer()
  }, [clearMotionEnterFrames, clearMotionExitTimer])

  useEffect(() => {
    if (!motionMounted || typeof document === 'undefined') return undefined
    const html = document.documentElement
    const body = document.body
    const previousBodyOverflow = body.style.overflow
    const previousHtmlOverflow = html.style.overflow
    const previousOverscroll = html.style.overscrollBehavior
    body.style.overflow = 'hidden'
    html.style.overflow = 'hidden'
    html.style.overscrollBehavior = 'none'
    return () => {
      body.style.overflow = previousBodyOverflow
      html.style.overflow = previousHtmlOverflow
      html.style.overscrollBehavior = previousOverscroll
    }
  }, [motionMounted])

  useEffect(() => {
    if (!open || !rawUserId) return undefined

    let alive = true
    const cached = getCachedUserInfo(rawUserId)
    if (cached) {
      // Stale-while-revalidate: render the cached profile immediately, refresh the
      // lightweight counters/presence slice, and repair legacy cache entries without bio.
      setData(cached)
      applyPresencePayload(cached)
      setStatus('ready')
      setError(null)
      fetchUserStatsSlice(rawUserId)
        .then((payload) => {
          if (!alive || !payload) return
          setData((current) => {
            const currentId = String(current?.accountId || '').trim()
            const nextId = String(payload?.accountId || '').trim()
            if (currentId && nextId && currentId !== nextId) return current
            return payload
          })
          applyPresencePayload(payload)
        })
        .catch(() => {})
      if (!String(cached?.about || '').trim()) {
        fetchUserInfo(rawUserId, { force: true })
          .then((payload) => {
            if (!alive || !payload) return
            setData(payload)
            applyPresencePayload(payload)
          })
          .catch(() => {})
      }
      return () => { alive = false }
    }

    setStatus('loading')
    setError(null)
    setData(null)
    setPresenceOnline(false)
    fetchUserInfo(rawUserId)
      .then((payload) => {
        if (!alive) return
        setData(payload)
        applyPresencePayload(payload)
        setStatus('ready')
      })
      .catch((err) => {
        if (!alive) return
        setError(err?.message || 'error')
        setStatus('error')
      })

    return () => { alive = false }
  }, [applyPresencePayload, fetchUserInfo, fetchUserStatsSlice, getCachedUserInfo, open, rawUserId])

  useEffect(() => {
    if (!open || !rawUserId) return
    setTranslatedBio(null)
    setShowOriginal(false)
    setTranslateBusy(false)
  }, [rawUserId, open])

  useEffect(() => {
    if (!open) return
    const stopPageKeys = (event) => {
      if (!['PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown', ' '].includes(event.key)) return
      if (popoverRef.current?.contains?.(event.target)) return
      event.preventDefault()
    }
    document.addEventListener('keydown', stopPageKeys, true)
    return () => document.removeEventListener('keydown', stopPageKeys, true)
  }, [open])

  const handleToggleTranslate = useCallback(
    async (e) => {
      e?.preventDefault?.()
      e?.stopPropagation?.()
      registerAction()
      if (!data?.about) return
      if (translatedBio) {
        setBioTranslationAnimation((prev) => ({
          src: `${String(rawUserId || '')}\u0000${String(data.about || '')}`,
          key: prev.key + 1,
        }))
        setShowOriginal((prev) => !prev)
        return
      }
      setTranslateBusy(true)
      try {
        const translated = await translateText(data.about)
        setTranslatedBio(translated)
        setBioTranslationAnimation((prev) => ({
          src: `${String(rawUserId || '')}\u0000${String(data.about || '')}`,
          key: prev.key + 1,
        }))
        setShowOriginal(false)
      } finally {
        setTranslateBusy(false)
      }
    },
    [data?.about, rawUserId, registerAction, translatedBio],
  )

  const openUserBranch = useCallback(
    (mode) => {
      const userId = String(data?.accountId || rawUserId || '').trim()
      if (!userId) return
      registerAction()
      notifySearchPopoverNavigation(mode)
      if (mode === 'posts') {
        onOpenUserPosts?.({ userId })
      } else if (mode === 'topics') {
        onOpenUserTopics?.({ userId })
      }
      if (String(userPreview?.sourceKind || '').startsWith('metamarket-')) {
        try { window.__QL7_CLOSE_METAMARKET__?.() } catch {}
      }
      onClose?.()
    },
    [data?.accountId, notifySearchPopoverNavigation, onClose, onOpenUserPosts, onOpenUserTopics, rawUserId, registerAction, userPreview?.sourceKind],
  )

  const openSubscriptions = useCallback(
    (event) => {
      event?.preventDefault?.()
      event?.stopPropagation?.()
      const uid = String(data?.accountId || rawUserId || '').trim()
      if (!uid) return
      registerAction()
      notifySearchPopoverNavigation('subscriptions')
      onOpenSubscriptions?.({ userId: uid, initialMode: 'followers' })
      onClose?.()
    },
    [data?.accountId, notifySearchPopoverNavigation, onClose, onOpenSubscriptions, rawUserId, registerAction],
  )

  const openGiftFlow = useCallback((event) => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    registerAction()
    notifySearchPopoverNavigation('gift')
    const recipientId = String(data?.accountId || userPreview?.userId || rawUserId || '').trim()
    if (!recipientId) return
    try {
      window.dispatchEvent(new CustomEvent('metamarket:open', {
        detail: {
          source: 'user-info-gift',
          initialMode: 'collections',
          giftFlow: true,
          viewerId: readViewerId(),
          recipient: {
            userId: recipientId,
            nickname: String(data?.nickname || userPreview?.nickname || '').trim(),
            avatar: String(data?.icon || userPreview?.avatar || userPreview?.icon || '').trim(),
            icon: String(data?.icon || userPreview?.icon || userPreview?.avatar || '').trim(),
            vipActive: typeof data?.vipActive === 'boolean' ? data.vipActive : !!userPreview?.vipActive,
            sourceKind: String(userPreview?.sourceKind || 'user-info-popover'),
            sourceId: String(userPreview?.sourceId || ''),
          },
        },
      }))
    } catch {}
    onOpenGiftFlow?.({ recipientId })
    onClose?.()
  }, [data?.accountId, data?.icon, data?.nickname, data?.vipActive, notifySearchPopoverNavigation, onClose, onOpenGiftFlow, rawUserId, registerAction, userPreview])

  const openDirectMessage = useCallback((event) => {
    event?.preventDefault?.()
    event?.stopPropagation?.()

    const targetUserId = String(
      data?.accountId ||
      resolveProfileAccountId(rawUserId) ||
      rawUserId ||
      '',
    ).trim()

    if (!targetUserId) return

    registerAction()

    // Не держим UserInfoPopover под auth overlay.
    // Сам переход в DM ниже произойдёт только после подтверждённой авторизации.
    // Здесь presentation-exit намеренно пропускается: auth overlay должен получить экран сразу.
    motionSkipExitRef.current = true
    onClose?.()

    void runAuthorizedClientAction({
      actionKey: `forum-user-info-dm:${targetUserId}`,
      source: 'forum-user-info-dm',
      action: () => {
        notifySearchPopoverNavigation('dm')

        const onForum = /^\/forum(?:\/|$)/u.test(String(window.location?.pathname || ''))
        if (onForum) {
          clearPendingForumDmUserId()
          try {
            window.dispatchEvent(new CustomEvent('forum:head-hide-once'))
            window.dispatchEvent(new CustomEvent('inbox:open-dm', {
              detail: {
                userId: targetUserId,
                source: 'user-info-popover',
              },
            }))
          } catch {}
          onOpenDirectMessage?.({ userId: targetUserId, navigationHandled: true })
          return
        }

        writePendingForumDmUserId(targetUserId)
        onOpenDirectMessage?.({ userId: targetUserId, navigationHandled: true })
        window.location.assign(buildForumDmHref(targetUserId))
      },
    })
  }, [
    data?.accountId,
    notifySearchPopoverNavigation,
    onClose,
    onOpenDirectMessage,
    rawUserId,
    registerAction,
  ])

  const handleMotionTransitionEnd = useCallback((event) => {
    if (event.target !== event.currentTarget) return
    if (event.propertyName !== 'transform') return
    if (!open && motionPhase === 'exit') finishMotionExit()
  }, [finishMotionExit, motionPhase, open])

  const hasLiveTarget = !!(open && String(rawUserId || '').trim())
  const shouldRenderMotion = motionMounted || hasLiveTarget
  if (!shouldRenderMotion) return null

  const liveTargetId = String(rawUserId || '').trim()
  const visualTargetMatches = !open || (!!liveTargetId && liveTargetId === motionUserId)
  const visualMotionPhase = motionMounted ? motionPhase : 'pre-enter'
  const stats = data?.stats || {}
  const showTranslated = translatedBio && !showOriginal
  const displayBio = showTranslated ? translatedBio : data?.about || ''
  const hasBio = Boolean(String(data?.about || '').trim())
  const bioTranslationSource = `${String(rawUserId || '')}\u0000${String(data?.about || '')}`
  const bioTranslationAnimationEnabled = !!data?.about && bioTranslationAnimation.src === bioTranslationSource
  const targetAccountId = String(data?.accountId || resolveProfileAccountId(rawUserId) || rawUserId || '').trim()
  const viewerAccountId = String(resolveProfileAccountId(viewerId) || viewerId || '').trim()
  const targetIdentityCandidates = Array.from(new Set([
    targetAccountId,
    String(rawUserId || '').trim(),
    targetAccountId.toLowerCase(),
    String(rawUserId || '').trim().toLowerCase(),
  ].filter(Boolean)))
  const isSubscribed = targetIdentityCandidates.some((identity) => starredAuthors?.has?.(identity))
  const isViewerProfile = !!viewerAccountId && targetIdentityCandidates.some(
    (identity) => identity.toLowerCase() === viewerAccountId.toLowerCase(),
  )
  const canToggleSubscription = !isViewerProfile && !!targetAccountId && typeof onToggleStar === 'function'
  const nickname = String(data?.nickname || userPreview?.nickname || '').trim() || 'Quantum User'
  const avatar = String(data?.icon || userPreview?.avatar || userPreview?.icon || '').trim()

  const popover = (
    <div
      className="userInfoModalOverlay"
      data-motion={visualMotionPhase}
      aria-hidden={visualMotionPhase === 'exit' ? 'true' : undefined}
      style={{
        zIndex: USER_INFO_STACK_Z,
        visibility: visualTargetMatches ? 'visible' : 'hidden',
        '--user-info-enter-ms': `${USER_INFO_ENTER_MS}ms`,
        '--user-info-exit-ms': `${USER_INFO_EXIT_MS}ms`,
      }}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
    <div
      ref={popoverRef}
      className={presenceOnline ? 'userInfoPopover userInfoPopover--onlinePulse' : 'userInfoPopover'}
      data-ql7-visual-scope="popover"
      data-motion={visualMotionPhase}
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-info-popover-title"
      onTransitionEnd={handleMotionTransitionEnd}
      onPointerDown={registerAction}
      onKeyDown={registerAction}
      onClick={(event) => {
        event.stopPropagation()
        registerAction()
      }}
    >
      <button
        type="button"
        className="userInfoCloseBtn"
        aria-label={t?.('forum_close') || 'Close'}
        title={t?.('forum_close') || 'Close'}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          registerAction()
          onClose?.()
        }}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="8.6" className="userInfoCloseOrbit" />
          <path d="M8.2 8.2l7.6 7.6M15.8 8.2l-7.6 7.6" className="userInfoCloseCross" />
        </svg>
      </button>
      {data?.officialStatus === true && (
        <span className="userInfoOfficialStatus userInfoOfficialStatus--edge" data-ql7-official-status="true">
          <img src="/status/official.png" alt="Official" draggable="false" />
          <span className="userInfoOfficialShine" aria-hidden="true" />
        </span>
      )}
      <header className="userInfoIdentityHeader">
        <div className="userInfoAvatarFrame ql7AvatarBadgeHost" aria-hidden="true">
          <span className="userInfoAvatarHalo" />
          {isAvatarImageSource(avatar) ? (
            <img className="userInfoAvatarImage" src={avatar} alt="" draggable="false" />
          ) : (
            <span className="userInfoAvatarFallback">{avatar || nickname.slice(0, 1).toUpperCase()}</span>
          )}
          <AvatarBadgeOverlay vipActive={!!data?.vipActive} showInfo={false} className="userInfoAvatarVipOverlay" />
          <span className={presenceOnline ? 'userInfoPresenceDot isOnline' : 'userInfoPresenceDot'} />
        </div>
        <div className="userInfoIdentityCopy">
          <span className="userInfoIdentityKicker">QL7 · QUANTUM PROFILE</span>
          <div className="userInfoNameRow">
            <h2 id="user-info-popover-title" className="userInfoNickname" translate="no">
              <span className="nick-badge nick-animate userInfoNickBadge">
                <ForumNickText className="nick-text truncate" textValue={nickname}>{nickname}</ForumNickText>
              </span>
            </h2>
            {canToggleSubscription && (
              <div className="userInfoInlineFollow">
                <StarButton
                  on={isSubscribed}
                  onClick={() => {
                    registerAction()
                    void onToggleStar?.(targetAccountId)
                  }}
                  title={isSubscribed ? t?.('forum_subscribed') : t?.('forum_subscribe_author')}
                />
              </div>
            )}
          </div>
        </div>
      </header>
      <div className="userInfoQuickActions">
        <div className="userInfoActionGroup">
          <button
            type="button"
            className="userInfoCircleActionBtn userInfoCircleActionBtn--dm userInfoDmBtn"
            onClick={openDirectMessage}
            title={t?.('inbox_tab_messages')}
            aria-label={t?.('inbox_tab_messages')}
          >
            <svg viewBox="0 0 24 24" aria-hidden>
              <path d="M3 7h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" stroke="currentColor" strokeWidth="1.6" fill="none" />
              <path
                d="M3 7l9 6 9-6"
                stroke="currentColor"
                strokeWidth="1.6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <img
            className="userInfoActionCore"
            src="/ql7-transaction-core.png"
            alt=""
            aria-hidden="true"
            draggable="false"
          />
          <button
            type="button"
            className="userInfoCircleActionBtn userInfoCircleActionBtn--gift userInfoGiftBtn"
            onClick={openGiftFlow}
            title={t?.('metamarket_gift_open_title')}
            aria-label={t?.('metamarket_gift_open_aria')}
          >
            <MetaMarketGiftIcon />
          </button>
        </div>
      </div>
      {status === 'loading' && (
        <>
          <div className="userInfoBioRow userInfoBioSkeletonRow" aria-hidden="true">
            <div className="userInfoBioHeader">
              <div className="userInfoBioLabelWrap">
                <span className="userInfoSkeleton userInfoSkeleton--bioLabel" />
                <span className="userInfoBioLabelRail" />
              </div>
              <div className="userInfoTranslateSlot">
                <span className="userInfoSkeleton userInfoSkeleton--translate" />
              </div>
            </div>
          </div>
          <div className="userInfoBioTextSkeleton" aria-hidden="true">
            <span className="userInfoSkeleton userInfoSkeleton--bioLine" />
            <span className="userInfoSkeleton userInfoSkeleton--bioLineShort" />
          </div>
        </>
      )}
      {hasBio && (
        <>
          <div className="userInfoBioRow">
            <div className="userInfoBioHeader">
              <div className="userInfoBioLabelWrap">
                <div className="userInfoBioLabel">{t?.('forum_user_popover_bio')}:</div>
                <span className="userInfoBioLabelRail" aria-hidden="true" />
              </div>
              <div className="userInfoTranslateSlot">
                <button
                  type="button"
                  className="userInfoTranslateToggle"
                  onClick={handleToggleTranslate}
                  disabled={translateBusy || status !== 'ready'}
                >
                  {translateBusy ? (
                    <>
                      <span>{t?.('forum_user_popover_loading')}</span>
                      <span className="userInfoTranslateShimmer" aria-hidden="true" />
                    </>
                  ) : (
                    <span>{showTranslated ? t?.('forum_user_popover_show_original') : t?.('forum_user_popover_translate')}</span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {status === 'ready' && (
            <TypewriterText
              as="div"
              className="userInfoBioText aboutText--live"
              text={displayBio}
              animate={bioTranslationAnimationEnabled}
              animationKey={bioTranslationAnimation.key}
              renderHtml={safeRich}
            />
          )}
        </>
      )}

      {status === 'error' && (
        <div className="text-sm text-red-200">
          <div>{t?.('forum_user_popover_error')}</div>
          <button type="button" className="userInfoTranslateToggle" onClick={handleRetry}>
            {t?.('forum_retry')}
          </button>
        </div>
      )}

      <div className="userInfoRail" aria-hidden="true" />
      <div className="userInfoStats" aria-busy={status === "loading"}>
        {status === 'loading' ? (
          <>
            <div className="userInfoStat userInfoStat--stars userInfoStat--skeletonFamily" aria-hidden="true">
              <span className="userInfoSkeleton userInfoSkeleton--familyTitle" />
              <div className="userInfoSkeletonFamilyMeta">
                <span className="userInfoSkeleton userInfoSkeleton--familyPill" />
                <span className="userInfoSkeleton userInfoSkeleton--familyArrow" />
              </div>
            </div>
            {['posts', 'topics', 'reactions'].map((kind) => (
              <div className="userInfoStat userInfoStat--skeletonStat" key={kind} aria-hidden="true">
                <span className="userInfoSkeleton userInfoSkeleton--statLabel" />
                <span className="userInfoSkeleton userInfoSkeleton--statRail" />
                <span className="userInfoSkeleton userInfoSkeleton--statValue" />
              </div>
            ))}
          </>
        ) : (
          <>
            <button
              type="button"
              className="userInfoStat userInfoStat--action userInfoStat--stars"
              onClick={openSubscriptions}
              title={t?.('forum_subscriptions_open_followers') || t?.('forum_user_popover_stars')}
            >
              <QuantumFamilyBadgeTitleSvg className="userInfoQuantumTitleSvg" />
              <div className="userInfoStatLabel">
                <QuantumFamilyCounterPill
                  className="userInfoQuantumPill"
                  followersCount={stats.followers}
                  followingCount={stats.following}
                  formatCountFn={formatCount}
                />
                <span className="userInfoStarBadge" aria-hidden="true">
                  <span className="subsRing" aria-hidden="true" />
                  <span className="subsStar" aria-hidden="true">
                    ★
                  </span>
                </span>
                <span className="srOnly">{t?.('forum_user_popover_stars')}</span>
                <span className="userInfoStatArrow" aria-hidden="true">
                  <span className="userInfoStatArrowTail" />
                  <span className="userInfoStatArrowHead" />
                </span>
              </div>
              <div className="userInfoStatValue">
                <HydrateText value={formatCount(stats.followers)} />
              </div>
            </button>
            <button
              type="button"
              className="userInfoStat userInfoStat--action"
              onClick={() => openUserBranch('posts')}
              title={t?.('forum_user_popover_posts')}
            >
              <div className="userInfoStatLabel">
                {t?.('forum_user_popover_posts')}
                <span className="userInfoStatArrow" aria-hidden="true">
                  <span className="userInfoStatArrowTail" />
                  <span className="userInfoStatArrowHead" />
                </span>
              </div>
              <div className="userInfoStatValue">
                <HydrateText value={formatCount(stats.posts)} />
              </div>
            </button>
            <button
              type="button"
              className="userInfoStat userInfoStat--action"
              onClick={() => openUserBranch('topics')}
              title={t?.('forum_user_popover_topics')}
            >
              <div className="userInfoStatLabel">
                {t?.('forum_user_popover_topics')}
                <span className="userInfoStatArrow" aria-hidden="true">
                  <span className="userInfoStatArrowTail" />
                  <span className="userInfoStatArrowHead" />
                </span>
              </div>
              <div className="userInfoStatValue">
                <HydrateText value={formatCount(stats.topics)} />
              </div>
            </button>
            <div className="userInfoStat">
              <div className="userInfoStatLabel">{t?.('forum_user_popover_likes')}</div>
              <div className="userInfoStatValue">
                <HydrateText value={formatCount(stats.likes)} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
    </div>
  )

  return createPortal(popover, document.body)
}
